import dynamic from "next/dynamic";
import classNames from "classnames";
import { Fragment, useEffect, useMemo, useRef, useState, useContext } from "react";
import { useSelector } from "react-redux";
import isEmpty from "lodash/isEmpty";
import debounce from "lodash/debounce";

import { useAuthQuery } from "@stadium/services/apollo-client";
import { useFeatureFlag } from "@stadium/services/launch-darkly";

import ShimmerWrapper from "@stadium/ui/src/components/loaders/shimmer-wrapper";
import Multimedia from "@sharedComponents/multimedia";
import ChooseMoreSwagBanner from "@sharedComponents/banner/choose-more-swag";

import {
  getAllMenuTaxons,
  getSelectedMenuItem,
  getParentCategoryFromCategory,
  getTaxonIdsRecursively,
} from "@helpers/menu";
import { getLockerShopESQueryVars } from "@helpers/store";
import { convertPrice } from "@helpers/currency";
import { isGlobalCountry } from "@stadium/helpers/country/utils";
import { hyphenatedStringToReadable } from "@stadium/helpers/string";
import { getUserDesignedStoreIds, getFilterQueryVariables } from "@helpers/products/search";

import {
  getMenu,
  getMenuAllChildren,
  getMenuCodeMapping,
  getMenuIsLoaded,
} from "@store/selectors/menu";
import { getCountry } from "@store/selectors/country";
import {
  getIsLockerShop,
  getIsLegacyLockerShop,
  getStoreBalance,
  getStoreCloneId,
  getStoreID,
  getStoreParentId,
  getStoreSyncedToES,
  getCommonElasticSearchStoreVariables,
  getIsPricelessShop,
} from "@store/selectors/store";

import { CurrentDeviceContext } from "@stadium/ui/src/contexts/responsive-content";
import Product from "@sharedComponents/cards/product";
import ProductListingHeader from "@components/store/products/listing-header";
import ShadowCard from "@sharedComponents/cards/shadow-card";
import ListingPagination from "@components/store/products/listing-pagination";
import FilterPills from "@components/store/products/filter-pills";
import { GetStartedBanner } from "@components/preview-catalog/banners";
import { useStoreContext } from "@components/store/provider";
import ScrollToTopBtn from "@stadium/ui/src/components/scroll-to-top-button";
import OldListing from "@components/store/products/listing/listing/old-listing";
import SearchInput from "@stadium/shared/src/components/filter/search-input";
import { AskAnExpertHelp } from "@stadium/shared/src/components/filter";
import { FilterType } from "@stadium/shared/src/components/filter/types";

import { getStoreCurrencyCode } from "@selectors/store";
import { getPricelessProductFetchVariables } from "@selectors/product";
import { getConversionRates } from "@stadium/redux-utils/selectors/configSelector";

import { useConfig } from "@stadium/hooks/lib/use-config";
import {
  selectFilterCurrentPage,
  selectFilterPerPage,
  makeFilterSort,
  selectAppliedFiltersCount,
} from "@stadium/redux-utils/selectors/filterSelector";
import { useFilter } from "@stadium/shared/src/hooks/use-filter";
import useShowNewHeader from "@hooks/use-show-new-header";
import { usePreviewModeContext } from "@contexts/preview-mode-context";
import useSearchProductList from "@hooks/search/use-fetch-base-product-list";
import useFilterConfig from "@hooks/search/use-filter-config";
import { useCreateShopContext } from "@contexts/create-shop-context";
import { useShopAnalyticsContext } from "@hooks/analytics/use-shop-analytics-context";
import { useCreateSwagContext } from "@contexts/create-swag-context";

import { fetchUserDesignedStoreConfig } from "@graphql/queries/stores/auth-based-params";

import { SystemFilterKeys } from "@stadium/redux-utils/constants/filter";

import notFoundImg from "@images/svg/no-result.svg";

import { US_CURRENCY_CODE } from "@constants/store";
import {
  MENU_CATEGORY_FILTER_KEY,
  RECOMMENDED_SORT_ITEM,
  LOCKER_INVENTORY_FILTER_TYPE,
  ITEM_TYPE_FILTER_KEY,
  PRODUCTION_DAYS_FILTER_KEY,
  DESIGNED_TYPE_FILTER_KEY,
  PRICE_FILTER_KEY,
} from "@constants/products/listing";
import { WAREHOUSE_PRODUCT } from "@constants/products/detail";
import { addSwagToShopBannerData } from "@constants/banner";
import { MY_DESIGNED_SWAG_LOCKER, PREDEFINED_MENU_ITEMS } from "@constants/menu";
import { ALL_PRODUCTS, ALL_SUBCATEGORY_CODE } from "@constants/routes";
import { LISTING_SECTION_REF } from "@components/store/products/listing";

import { LOWEST_PRICE_RANGE_FILTER } from "@stadium/shared/src/constants/stores/routes";
import { STICKY_HEADER_REF } from "@stadium/ui/src/components/header";
import { ANALYTICS_EVENT_ACTION_CHANGED } from "@stadium/services/analytics/constants/default";
import { ANALYTICS_TARGET_PAGINATION } from "@constants/analytics/filter";

import ListingScrollTracker from "@components/store/products/listing-scroll-tracker";

import styles from "../main.module.scss";

const AddSwagToShopBanner = dynamic(() => import("@sharedComponents/banner/add-swag-to-shop"));

const emptyObj = {};
const emptyArr = [];
const BANNER_POSITION = 8;

const HIDE_FILTER_KEYS = [MENU_CATEGORY_FILTER_KEY, PRODUCTION_DAYS_FILTER_KEY];
const HIDE_FILTER_KEYS_IN_NEW_SWAGMAGIC = [PRODUCTION_DAYS_FILTER_KEY];
const DEFAULT_FILTER_TYPES = {
  [PRICE_FILTER_KEY]: FilterType.SINGLE_SELECT,
};

const NoProductsView = ({ parentCategory, childCategory }) => {
  const isPricelessShop = useSelector(getIsPricelessShop);
  const { isNewShopCreationFlow } = useCreateShopContext();
  const { isSwagCreationShopFlow } = useCreateSwagContext();
  return (
    <div className={styles.noProductsView}>
      <Multimedia src={notFoundImg} type="image" width={138} height={138} alt="No results found" />
      <div className={styles.noResultFound}>
        {isNewShopCreationFlow && isPricelessShop ? (
          <>
            Looks like there are no products in this price range.{" "}
            <span className={styles.resetFilter}>Adjust your price range to see more options.</span>
          </>
        ) : (
          <>
            No results found under <strong>{childCategory?.name || parentCategory?.name}</strong>.
            <span className={styles.resetFilter}>
              {" "}
              Try resetting your filters{isSwagCreationShopFlow ? " and preferred swag colors" : ""}
              .
            </span>
          </>
        )}
      </div>
    </div>
  );
};

const ProductListingShimmerLoader = ({ showLeftPanel, perPage }) => {
  const { isNewSwagmagicHost } = useStoreContext();
  return (
    <ShimmerWrapper
      length={perPage}
      cardClass={styles.colCard}
      wrapperClass={`${styles.rowWrapper} ${
        isNewSwagmagicHost ? styles.productCardsWrapperV2Shimmer : ""
      } ${showLeftPanel ? styles.productCardsWrapper : styles.productCardsWrapperFive}`}
    >
      <ShadowCard />
    </ShimmerWrapper>
  );
};

const ProductListing = ({
  showLeftFilter,
  showLeftPanel,
  topBannerPresent,
  isKitFlow = false,
  renderFilter,
}) => {
  const { isReady } = useFeatureFlag();

  const {
    isPreviewCatalogMode,
    onlyHasAllProductsMenu,
    category: categoryCode,
    subCategory: subCategoryCode,
    isNewSwagmagicHost,
    menuIdentifier,
    pageType,
  } = useStoreContext();
  const {
    onChange: onFilterChange,
    data: filterState,
    clear: clearFilters,
    init: initFilter,
  } = useFilter();

  const showNewHeader = useShowNewHeader();

  const { parentCategoryCode, activeCategoryCode } = getParentCategoryFromCategory(categoryCode);

  const activeHasFiltersApplied = useSelector(selectAppliedFiltersCount);

  const { isNewShopCreationFlow } = useCreateShopContext();

  const { previewMode, getStoreUpdatedAt } = usePreviewModeContext();
  const storeBalance = useSelector(getStoreBalance);
  const [childCategory, setChildCategory] = useState();
  const [parentCategory, setParentCategory] = useState();
  const [searchQuery, setSearchQuery] = useState("");
  const syncedToEs = useSelector(getStoreSyncedToES);
  const storeParentId = useSelector(getStoreParentId);
  const storeId = useSelector(getStoreID);
  const menu = useSelector(getMenu);
  const menuCodeMapping = useSelector(getMenuCodeMapping);
  const country = useSelector(getCountry);
  const storeCurrencyCode = useSelector(getStoreCurrencyCode);
  const conversionRates = useConfig(getConversionRates);
  const storeCloneId = useSelector(getStoreCloneId);
  const commonESVariables = useSelector(getCommonElasticSearchStoreVariables);
  const isLockerShop = useSelector(getIsLockerShop);
  const isLegacyLockerShop = useSelector(getIsLegacyLockerShop);
  const allChildren = useSelector(getMenuAllChildren);
  const pricelessVariables = useSelector(getPricelessProductFetchVariables);
  const isMenuLoaded = useSelector(getMenuIsLoaded);

  const currentPage = useSelector(selectFilterCurrentPage);
  const perPage = useSelector(selectFilterPerPage);
  const currentSortBy = useSelector(useMemo(() => makeFilterSort(), []));

  const showFiltersDropdown = !showLeftFilter && !isNewShopCreationFlow;
  const { isDesktopDevice, isMobileDevice } = useContext(CurrentDeviceContext);

  const shopAnalyticsContext = useShopAnalyticsContext();

  const { data: designedStoreConfig, loading: isFetchingDesignedStore } = useAuthQuery(
    fetchUserDesignedStoreConfig,
    {
      skip: !(isLockerShop || isKitFlow) || !isReady,
    },
  );

  const updatedDesignedStoreConfig = useMemo(() => {
    const {
      allLockerSwagList = [],
      allOnDemandSwagList = [],
      lockerSwagList = [],
      onDemandSwagList = [],
    } = designedStoreConfig?.user || {};

    return {
      allLockerSwagIds: allLockerSwagList.map(({ id }) => +id),
      allOnDemandSwagIds: allOnDemandSwagList.map(({ id }) => +id),
      lockerSwagIds: lockerSwagList.map(({ id }) => +id),
      onDemandSwagIds: onDemandSwagList.map(({ id }) => +id),
    };
  }, [designedStoreConfig]);

  useEffect(() => {
    const parentItem = getSelectedMenuItem(menuCodeMapping[parentCategoryCode], activeCategoryCode);
    const childItem = parentItem && getSelectedMenuItem(parentItem, subCategoryCode);

    if (parentItem?.id !== parentCategory?.id) {
      setParentCategory(parentItem);
    }
    if (childItem?.id !== childCategory?.id) {
      setChildCategory(childItem);
    }
  }, [
    activeCategoryCode,
    childCategory,
    menuCodeMapping,
    parentCategory,
    parentCategoryCode,
    subCategoryCode,
  ]);

  const { taxonIds, withSwag } = useMemo(() => {
    const menuLinkedResources =
      subCategoryCode === ALL_SUBCATEGORY_CODE && parentCategory
        ? getTaxonIdsRecursively(parentCategory)
        : getTaxonIdsRecursively(childCategory);

    const ids = menuLinkedResources.map((resource) => +resource?.id);
    const withSwagVal = menuLinkedResources.some((resource) => resource?.forSwag);
    const newTaxonIds = ids.filter(Boolean);

    return { taxonIds: newTaxonIds.length ? newTaxonIds : emptyArr, withSwag: withSwagVal };
  }, [childCategory, parentCategory, subCategoryCode]);

  const headerCategoryLabel = useMemo(() => {
    if (menuIdentifier) {
      return hyphenatedStringToReadable(menuIdentifier);
    }
    return childCategory?.name || parentCategory?.name || "";
  }, [menuIdentifier, childCategory, parentCategory]);

  const budget = parseInt(
    convertPrice({
      value: storeBalance || 0,
      from: storeCurrencyCode,
      to: US_CURRENCY_CODE,
      conversionRates,
    }),
  );

  const designedStoreIdsForListing = useMemo(
    () =>
      getUserDesignedStoreIds({
        userConfig: updatedDesignedStoreConfig,
        selectedItemTypes: filterState?.[ITEM_TYPE_FILTER_KEY] || emptyArr,
        designedItemTypes: filterState?.[DESIGNED_TYPE_FILTER_KEY] || emptyArr,
        isLockerShop,
      }),
    [
      // eslint-disable-next-line react-hooks/use-memo
      filterState?.[ITEM_TYPE_FILTER_KEY],
      // eslint-disable-next-line react-hooks/use-memo
      filterState?.[DESIGNED_TYPE_FILTER_KEY],
      updatedDesignedStoreConfig,
      isLockerShop,
    ],
  );

  const { code = "" } = parentCategory || emptyObj;
  const isMyDesignedSwag = code === MY_DESIGNED_SWAG_LOCKER;

  // TODO: Build a query builder to reuse in search and listing
  const queryVariables = useMemo(() => {
    const getInitialVariables = () => ({
      storeId: isKitFlow ? storeId : storeParentId,
      designedStoreIds: [storeId],
      countryCode: isGlobalCountry(country) ? "" : country.iso?.toLowerCase(),
      globalZone: isGlobalCountry(country),
      ...(menuIdentifier ? { menuIdentifier } : { taxonIds }),
      withSwag,
      searchQuery,
      budget,
      page: +currentPage,
      perPage,
      sortBy: currentSortBy,
      skipWarehouse: !syncedToEs && !!storeCloneId,
      storeUpdatedAt: getStoreUpdatedAt(),
      showVariantsAsProducts: true,
      ...(isKitFlow && { bulkAvailable: false }),
      withOutOfStock: isKitFlow,
      ...commonESVariables,
      ...getLockerShopESQueryVars({ isLockerShop: isLegacyLockerShop }),
      ...pricelessVariables,
      ...getFilterQueryVariables(filterState),
    });

    const applyMenuModifiers = (vars) => {
      const isSortByRecommended = currentSortBy === RECOMMENDED_SORT_ITEM.id;
      if (!menuIdentifier && (!isLockerShop || isSortByRecommended)) {
        vars.menuId = +menu.id;
        vars.menuItemId = +childCategory?.id || +parentCategory?.id;
      }

      const { allMenuTaxonIds, anyMenuWithSwag } = getAllMenuTaxons(allChildren);
      if (code === ALL_PRODUCTS) {
        vars.taxonIds = allMenuTaxonIds;
        vars.withSwag = anyMenuWithSwag;
      }
      return { allMenuTaxonIds };
    };

    const applyKitFlowModifiers = (vars) => {
      if (!isKitFlow) return;

      if (filterState?.[ITEM_TYPE_FILTER_KEY]?.includes(LOCKER_INVENTORY_FILTER_TYPE.id)) {
        vars.type = WAREHOUSE_PRODUCT;
      }
    };

    const applyLockerShopModifiers = (vars, allMenuTaxonIds) => {
      if ((isLockerShop && isMyDesignedSwag) || isKitFlow) {
        vars.designedStoreIds = designedStoreIdsForListing;
      }

      if (isLockerShop) {
        vars.optimizedSearch = true;
        vars.withSwag = true;
        if (isMyDesignedSwag) {
          vars.optimizedSearch = false;
          vars.taxonIds = allMenuTaxonIds;
          vars.storeId = null;
        }
      }
    };

    const variables = getInitialVariables();
    const { allMenuTaxonIds } = applyMenuModifiers(variables);
    applyKitFlowModifiers(variables);
    applyLockerShopModifiers(variables, allMenuTaxonIds);

    return variables;
  }, [
    country,
    currentSortBy,
    filterState,
    isKitFlow,
    storeId,
    storeParentId,
    taxonIds,
    searchQuery,
    withSwag,
    budget,
    currentPage,
    perPage,
    syncedToEs,
    storeCloneId,
    getStoreUpdatedAt,
    commonESVariables,
    isLegacyLockerShop,
    pricelessVariables,
    isLockerShop,
    menu?.id,
    childCategory?.id,
    parentCategory?.id,
    allChildren,
    code,
    designedStoreIdsForListing,
    isMyDesignedSwag,
    menuIdentifier,
  ]);

  const {
    loading: isProductsLoading,
    fetchedProducts,
    totalProducts,
    aggregations,
    getProductList,
    productsInventory,
    isInventoryFetching,
  } = useSearchProductList({ skipInventoryFetching: !isKitFlow });

  const debouncedGetProductList = useMemo(() => debounce(getProductList, 500), [getProductList]);

  const loading = isProductsLoading || isEmpty(filterState);

  const skipFetchIfNoStoreIds =
    (isKitFlow || isMyDesignedSwag) && !designedStoreIdsForListing?.length;

  useEffect(() => {
    if (
      (!onlyHasAllProductsMenu || menuIdentifier) &&
      isMenuLoaded &&
      !isFetchingDesignedStore &&
      (queryVariables.taxonIds?.length || queryVariables.menuIdentifier) &&
      !skipFetchIfNoStoreIds &&
      !isEmpty(filterState)
    ) {
      // Product list should only be fetched if there are taxonids
      debouncedGetProductList({
        variables: queryVariables,
      });
      return () => {
        debouncedGetProductList.cancel();
      };
    }
  }, [
    isMenuLoaded,
    queryVariables,
    onlyHasAllProductsMenu,
    isFetchingDesignedStore,
    skipFetchIfNoStoreIds,
    filterState,
    menuIdentifier,
  ]);

  useEffect(() => {
    initFilter();
    return () => clearFilters([]);
  }, []);

  // Scroll depth tracking handled by ListingScrollTracker component

  const filterConfig = useFilterConfig({
    aggregations,
    hideFilterKeys: isNewSwagmagicHost ? HIDE_FILTER_KEYS_IN_NEW_SWAGMAGIC : HIDE_FILTER_KEYS,
    defaultFilterTypes: DEFAULT_FILTER_TYPES,
  });

  const defaultPriceValue = useMemo(() => {
    const priceSection = filterConfig?.find((config) => config.id === PRICE_FILTER_KEY);
    return Array.isArray(priceSection?.options) ? priceSection.options[0]?.value : undefined;
  }, [filterConfig]);

  // Replace "lowest-price-range" placeholder with the actual first price range bucket
  const priceAutoSelectedRef = useRef(false);
  useEffect(() => {
    if (
      !priceAutoSelectedRef.current &&
      defaultPriceValue &&
      filterState?.[PRICE_FILTER_KEY] === LOWEST_PRICE_RANGE_FILTER
    ) {
      priceAutoSelectedRef.current = true;
      onFilterChange(FilterType.SINGLE_SELECT, {
        id: PRICE_FILTER_KEY,
        value: defaultPriceValue,
      });
    }
  }, [defaultPriceValue, filterState, onFilterChange]);

  const setPage = (pageNumber) => {
    onFilterChange(SystemFilterKeys.PAGE, { id: SystemFilterKeys.PAGE, value: pageNumber });
    window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
  };

  const addSwagToShopBannerPosition = useMemo(() => {
    return fetchedProducts.length > BANNER_POSITION
      ? BANNER_POSITION - 1
      : fetchedProducts.length - 1;
  }, [fetchedProducts]);

  const displayBannerVisible = useMemo(
    () =>
      previewMode &&
      (childCategory ? !!childCategory?.swagBannerAddable : !!parentCategory?.swagBannerAddable),
    [childCategory, parentCategory, previewMode],
  );

  const renderAddSwagToShopBanner = () => (
    <AddSwagToShopBanner key={addSwagToShopBannerData.name} data={addSwagToShopBannerData} />
  );

  const numOfTilesToShowCatalogBanner = isDesktopDevice ? 23 : 11;

  const renderProducts = (products, startIndex = 0) => {
    if (!products?.length) return "";
    return (
      <article
        className={classNames(styles.rowWrapper, {
          [styles.productCardsWrapper]: showLeftPanel,
          [styles.productCardsWrapperFive]: !showLeftPanel,
          [styles.productCardsWrapperV2]: isNewSwagmagicHost,
        })}
      >
        {products.map((product, index) => {
          const productQtyMapping = productsInventory[product?.id]?.total || emptyObj;

          return (
            <Fragment key={`product-${product.id}`}>
              <Product
                data={product}
                key={product.id}
                source="search"
                productTileClass={`${styles.productListingPageOnly} white-base-tile`}
                className={styles.colCard}
                imageLoadingStrategy="lazy"
                showInventoryStatus={!isInventoryFetching && isKitFlow}
                productQtyMapping={productQtyMapping}
                eventLocationAnalytics={{
                  track: true,
                  eventProperties: {
                    ...shopAnalyticsContext,
                    product_id: product.id,
                    product_name: product.name,
                    position_in_list: startIndex + index + 1,
                  },
                }}
              />
            </Fragment>
          );
        })}
      </article>
    );
  };

  const bannerPosition = (() => {
    if (displayBannerVisible || isKitFlow) {
      return addSwagToShopBannerPosition;
    }
    if (isPreviewCatalogMode) {
      return numOfTilesToShowCatalogBanner;
    }
    return fetchedProducts.length - 1;
  })();

  const productsAboveBanner = fetchedProducts.slice(0, bannerPosition + 1);
  const productsBelowBanner = fetchedProducts.slice(bannerPosition + 1);

  const isNoProductsAvailable =
    (onlyHasAllProductsMenu && !menuIdentifier) ||
    isEmpty(fetchedProducts) ||
    skipFetchIfNoStoreIds;

  const renderProductsListing = () => {
    const isFetchingProductsData =
      (!onlyHasAllProductsMenu &&
        !menuIdentifier &&
        taxonIds.length === 0 &&
        !PREDEFINED_MENU_ITEMS.includes(parentCategory?.code)) ||
      loading ||
      isFetchingDesignedStore;

    if (isFetchingProductsData) {
      return (
        <ProductListingShimmerLoader
          showLeftPanel={showLeftPanel}
          perPage={filterState?.[SystemFilterKeys.PER_PAGE]}
        />
      );
    }

    if (isNoProductsAvailable) {
      if (isKitFlow) {
        return activeHasFiltersApplied ? (
          <NoProductsView childCategory={childCategory} parentCategory={parentCategory} />
        ) : (
          <ChooseMoreSwagBanner hasNoProducts />
        );
      }
      return <NoProductsView childCategory={childCategory} parentCategory={parentCategory} />;
    }
    return (
      <ListingScrollTracker baseEventProperties={shopAnalyticsContext}>
        {renderProducts(productsAboveBanner, 0)}
        {isKitFlow && <ChooseMoreSwagBanner />}
        {displayBannerVisible && renderAddSwagToShopBanner()}
        {isPreviewCatalogMode && <GetStartedBanner />}
        {renderProducts(productsBelowBanner, productsAboveBanner.length)}
      </ListingScrollTracker>
    );
  };

  const title = (() => {
    if (!showNewHeader) {
      return childCategory?.name;
    }
    if (isMobileDevice || !fetchedProducts?.length || parentCategory?.code == ALL_PRODUCTS) {
      return "";
    }

    if (!topBannerPresent && subCategoryCode === ALL_SUBCATEGORY_CODE) {
      return parentCategory?.name;
    }

    if (!childCategory?.children?.length && childCategory?.depth === 2) {
      return childCategory?.name;
    }
  })();

  return (
    <div
      className={`${styles.listingContainer} ${
        isNewSwagmagicHost ? styles.listingContainerSW : ""
      }`}
      style={{ "--sticky-header-height": `${STICKY_HEADER_REF.current?.clientHeight + 60}px` }}
    >
      {typeof renderFilter === "function" && (
        <div className={styles.listingFilterSection}>
          {pageType !== "search" && (
            <SearchInput
              onDebouncedChange={setSearchQuery}
              className={styles.listingSearchInput}
              containerClass={styles.listingSearchInputContainer}
            />
          )}
          {showLeftFilter && isLockerShop && <AskAnExpertHelp />}
          {renderFilter(filterConfig)}
        </div>
      )}
      <section ref={LISTING_SECTION_REF} className={styles.pageSection}>
        <ProductListingHeader
          totalProducts={isNoProductsAvailable ? 0 : totalProducts}
          currentPage={currentPage}
          showFilters={showFiltersDropdown}
          showSortDropdown={!isNewShopCreationFlow}
          title={title}
          categoryLabel={headerCategoryLabel}
          isFetchingProducts={loading}
          filterConfig={showFiltersDropdown ? filterConfig : undefined}
        />
        {!isMobileDevice && <FilterPills config={filterConfig} />}
        <ScrollToTopBtn />
        {renderProductsListing()}
        {!loading && (
          <ListingPagination
            total={isNoProductsAvailable ? 0 : totalProducts}
            perPage={perPage}
            currentPage={currentPage}
            onPageChange={setPage}
            className="pagination"
            eventLocationAnalytics={{
              track: true,
              target: ANALYTICS_TARGET_PAGINATION,
              action: ANALYTICS_EVENT_ACTION_CHANGED,
              eventProperties: shopAnalyticsContext,
            }}
          />
        )}
      </section>
    </div>
  );
};

const ProductListingWrapper = (props) => {
  const { isNewSwagmagicHost } = useStoreContext();
  return isNewSwagmagicHost ? <ProductListing {...props} /> : <OldListing {...props} />;
};

export default ProductListingWrapper;
